'use client';

import { useRef, useState, useEffect } from 'react';
import { AddImageIcon, useToast } from '@/src/components/ui';
import Cropper, { ReactCropperElement } from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import { Input } from '@workspace/ui/components/input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@workspace/ui/components/dialog';
import { Button } from '@workspace/ui/components/button';
import { uploadImage } from '@/src/actions/storage';
import { cn } from '@workspace/ui/lib/utils';
import { IUITile } from '@/src/types/ITile';
import { useProfileUIStore } from '../../../store';
import { createTile } from '@/src/actions/tiles';
import { useEditingHoverControl } from '../../../hooks/useEditingHoverControl';
import { useClickOrDrag } from '../../../hooks/useClickOrDrag';
import { useTilesStore } from '../../../store/tiles-store';
import { LoadingOverlay } from '../LoadingOverlay';
import Image from 'next/image';


type Props = {
  tile: IUITile;
  isMyProfile?: boolean;
  isMobileView?: boolean;
  onHoverStateChange?: (shouldDisable: boolean) => void;
};

export const ImageTile = ({
  tile,
  isMyProfile = false,
  onHoverStateChange,
}: Props) => {
  const toast = useToast();
  const [imageUrl, setImageUrl] = useState<string | null>(tile.content || null);
  const [tempImage, setTempImage] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cropperRef = useRef<ReactCropperElement>(null);
  const setTileLoading = useTilesStore((state) => state.setTileLoading);

  const updateTile = useTilesStore((state) => state.updateTile);
  const isMobileView = useProfileUIStore((state) => state.isMobileView);

  const { handleStartEditing, handleStopEditing } =
    useEditingHoverControl(onHoverStateChange);

  const { onMouseDown, onTouchStart } = useClickOrDrag(() => {
    if (isMyProfile) {
      fileInputRef.current?.click();
    }
  });

  const handleFileChange = async (e: any) => {
    const file = e.target.files?.[0];
    if (!file || file.size > 4 * 1024 * 1024) return;
    if (file.type === 'image/gif' || file.type === 'image/webp') {
      const { data, error } = await uploadImage({ blob: file, type: 'tile' });

      if (error || !data?.url) {
        console.error(error?.message || 'Upload failed');
        toast.error('Failed to upload image');
        return;
      }

      const { iconName, ...tileWithoutIconName } = tile;
      const updated = await createTile({
        ...tileWithoutIconName,
        content: data.url,
        title: '',
      });

      if (!updated) {
        toast.error('Failed to save image');
        return;
      }

      updateTile(tile.i, {
        ...tile,
        ...updated,
        static: false,
      });

      setImageUrl(data.url);
      handleStopEditing();
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setTempImage(reader.result as string);
      setIsDialogOpen(true);
      updateTile(tile.i, {
        ...tile,
        static: false,
      });
      handleStartEditing();
    };
    reader.readAsDataURL(file);
  };

  const handleSaveImage = async () => {
    const cropper = cropperRef.current?.cropper;
    const canvas = cropper?.getCroppedCanvas({
      width: 500,
      height: 500,
    });

    if (!canvas) return;
    setTileLoading(tile.i, true);

    try {
      const dataUrl = canvas.toDataURL('image/png');
      const response = await fetch(dataUrl);
      const blob = await response.blob();
      const { data, error } = await uploadImage({ blob, type: 'tile' });
      if (error) {
        console.error(error.message);
        return;
      }

      if (data?.url) {
        const { iconName, ...tileWithoutIconName } = tile;
        const updated = await createTile({
          ...tileWithoutIconName,
          content: data.url,
          title: '',
        });

        if (!updated) {
          console.error('Failed to upload image');
          toast.error('Failed to upload image');
          return;
        }
        updateTile(tile.i, {
          ...tile,
          ...updated,
          static: false,
        });
        setImageUrl(data.url);
        setIsDialogOpen(false);
        setTempImage(null);
      }
    } catch (error) {
      toast.error('Error saving image');
    } finally {
      handleStopEditing();
    }
    setTileLoading(tile.i, false);
  };

  useEffect(() => {
    setImageUrl(tile.content || null);
  }, [tile.content]);

  if (tile.static) {
    return (
      <div
        className="flex flex-col items-center justify-center h-full w-full cursor-pointer"
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleStartEditing();
          fileInputRef.current?.click();
        }}
      >
        <Input
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept="image/*"
          onChange={handleFileChange}
        />
        <AddImageIcon />
        <p className="mt-3 text-sm font-semibold">Add image</p>
      </div>
    );
  }

  return (
    <>
      <div
        className={cn(
          'w-full h-full flex items-center justify-center relative group rounded-2xl overflow-hidden bg-transparent'
        )}
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
      >
        {imageUrl && (
          <Image
            src={imageUrl}
            alt="Tile image"
            className="w-full h-full object-cover bg-transparent"
            style={{ backgroundColor: 'transparent' }}
            width={500}
            height={500}
          />
        )}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="md:max-w-[600px] max-w-310px h-fit">
          <DialogHeader>
            <DialogTitle>Crop Your Image</DialogTitle>
          </DialogHeader>
          {tile.isLoading && <LoadingOverlay />}
          {tempImage && (
            <Cropper
              src={tempImage}
              aspectRatio={1}
              viewMode={1}
              guides={false}
              responsive={true}
              autoCropArea={1}
              background={false}
              checkOrientation={false}
              ref={cropperRef}
            />
          )}

          <DialogFooter className="flex justify-between mt-4">
            <Button
              variant="outline"
              onClick={() => {
                handleStopEditing();
                setIsDialogOpen(false);
                setTempImage(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveImage}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
